// Terrain layer: draws TerrainGeometry in ink at reduced opacity (02-SYMBOLOGY.md §8).
// The caller sets the canvas transform to world units; `cssPx` is the size of one CSS pixel
// in world units, so strokes stay the same thickness on screen at every zoom.
import type { Point } from '../hexgeom';
import { hash01 } from '../hexgeom';
import { chaikin, resample, type Polyline, type TerrainGeometry } from '../terrainGeometry';
import type { Ink } from '../theme';

export type TerrainStyle = Ink & {
  /** World units per CSS pixel at the scale being drawn. */
  cssPx: number;
};

/** Opacity per feature (02-SYMBOLOGY.md §8). */
export const TERRAIN_ALPHA = {
  contour: 0.35,
  contourLabel: 0.85,
  woods: 0.4,
  urban: 0.55,
  swamp: 0.5,
  water: 0.5,
  stream: 0.6,
  river: 0.6,
  riverFill: 0.18,
  road: 0.6,
  bridge: 0.8,
  border: 0.5,
} as const;

function tracePath(ctx: CanvasRenderingContext2D, pts: Point[], closed: boolean): void {
  if (pts.length === 0) return;
  ctx.moveTo((pts[0] as Point).x, (pts[0] as Point).y);
  for (let i = 1; i < pts.length; i++) ctx.lineTo((pts[i] as Point).x, (pts[i] as Point).y);
  if (closed) ctx.closePath();
}

function strokeLines(ctx: CanvasRenderingContext2D, lines: Polyline[]): void {
  ctx.beginPath();
  for (const l of lines) tracePath(ctx, l.pts, l.closed);
  ctx.stroke();
}

/** Shift a closed loop to its right (inward for our outlines) by d. */
function inset(pts: Point[], d: number): Point[] {
  const n = pts.length;
  return pts.map((p, i) => {
    const a = pts[(i - 1 + n) % n] as Point;
    const b = pts[(i + 1) % n] as Point;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    // Right of travel direction on a y-down screen: (-dy, dx).
    return { x: p.x - (dy / len) * d, y: p.y + (dx / len) * d };
  });
}

function tangentAt(pts: Point[], i: number): Point {
  const a = pts[Math.max(0, i - 1)] as Point;
  const b = pts[Math.min(pts.length - 1, i + 1)] as Point;
  const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  return { x: (b.x - a.x) / len, y: (b.y - a.y) / len };
}

export function outlinePath(ctx: CanvasRenderingContext2D, geo: TerrainGeometry): void {
  ctx.beginPath();
  tracePath(ctx, geo.outline, true);
}

export function drawTerrain(
  ctx: CanvasRenderingContext2D,
  geo: TerrainGeometry,
  s: TerrainStyle,
): void {
  const px = s.cssPx;
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  outlinePath(ctx, geo);
  ctx.clip();

  ctx.strokeStyle = s.ink;
  ctx.fillStyle = s.ink;

  // Contours
  ctx.globalAlpha = TERRAIN_ALPHA.contour;
  ctx.lineWidth = 1 * px;
  strokeLines(
    ctx,
    geo.contours.filter((c) => !c.index),
  );
  ctx.lineWidth = 1.8 * px;
  strokeLines(
    ctx,
    geo.contours.filter((c) => c.index),
  );
  drawContourLabels(ctx, geo, s);

  // Woods: scalloped outline + sparse stipple
  ctx.globalAlpha = TERRAIN_ALPHA.woods;
  ctx.lineWidth = 1.2 * px;
  ctx.beginPath();
  for (const loop of geo.woods) {
    const pts = resample(inset(chaikin(loop, true, 1), 0.1), true, 0.42);
    const n = pts.length;
    ctx.moveTo((pts[0] as Point).x, (pts[0] as Point).y);
    for (let i = 0; i < n; i++) {
      const a = pts[i] as Point;
      const b = pts[(i + 1) % n] as Point;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      // Bulge to the left of travel = away from the woods.
      ctx.quadraticCurveTo((a.x + b.x) / 2 + dy * 0.6, (a.y + b.y) / 2 - dx * 0.6, b.x, b.y);
    }
    ctx.closePath();
  }
  ctx.stroke();
  ctx.beginPath();
  const dot = 1.1 * px;
  for (const c of geo.centers.woods) {
    for (let k = 0; k < 4; k++) {
      const ang = hash01(c.col, c.row, k * 2) * Math.PI * 2;
      const rad = 0.15 + hash01(c.col, c.row, k * 2 + 1) * 0.45;
      const x = c.x + Math.cos(ang) * rad;
      const y = c.y + Math.sin(ang) * rad;
      ctx.moveTo(x + dot, y);
      ctx.arc(x, y, dot, 0, Math.PI * 2);
    }
  }
  ctx.fill();

  // Urban: small buildings on a loose grid, rotated a little per hex
  ctx.globalAlpha = TERRAIN_ALPHA.urban;
  const side = Math.max(0.17, 3 * px);
  ctx.beginPath();
  for (const c of geo.centers.urban) {
    const rot = (hash01(c.col, c.row, 7) - 0.5) * 0.5;
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    const roads = geo.roadDirs.get(`${c.col},${c.row}`) ?? [];
    for (let i = 0; i < 9; i++) {
      if (hash01(c.col, c.row, 20 + i) < 0.35) continue; // gaps between buildings
      const gx = ((i % 3) - 1) * 0.36;
      const gy = (Math.floor(i / 3) - 1) * 0.32;
      const x = gx * cos - gy * sin;
      const y = gx * sin + gy * cos;
      // Keep buildings off the road: skip any within the road corridor.
      const onRoad = roads.some((d) => {
        const along = x * d.x + y * d.y;
        const across = Math.abs(-x * d.y + y * d.x);
        return along > -0.12 && across < side / 2 + 0.1;
      });
      if (onRoad) continue;
      ctx.save();
      ctx.translate(c.x + x, c.y + y);
      ctx.rotate(rot);
      ctx.rect(-side / 2, -side / 2, side, side);
      ctx.restore();
    }
  }
  ctx.fill();

  // Swamp: tufts (short vertical strokes over a horizontal dash)
  ctx.globalAlpha = TERRAIN_ALPHA.swamp;
  ctx.lineWidth = 1 * px;
  ctx.beginPath();
  for (const c of geo.centers.swamp) {
    for (let k = 0; k < 3; k++) {
      const x = c.x + (hash01(c.col, c.row, 40 + k) - 0.5) * 0.9;
      const y = c.y + (k - 1) * 0.42 + (hash01(c.col, c.row, 50 + k) - 0.5) * 0.12;
      ctx.moveTo(x - 0.16, y);
      ctx.lineTo(x + 0.16, y);
      ctx.moveTo(x, y);
      ctx.lineTo(x, y - 0.13);
      ctx.moveTo(x - 0.08, y);
      ctx.lineTo(x - 0.1, y - 0.08);
      ctx.moveTo(x + 0.08, y);
      ctx.lineTo(x + 0.1, y - 0.08);
    }
  }
  ctx.stroke();

  // Lakes: outline + ripple lines inside
  ctx.globalAlpha = TERRAIN_ALPHA.water;
  for (const loop of geo.water) {
    const pts = chaikin(inset(loop, 0.06), true, 2);
    ctx.lineWidth = 1.2 * px;
    ctx.beginPath();
    tracePath(ctx, pts, true);
    ctx.stroke();
    ctx.save();
    ctx.beginPath();
    tracePath(ctx, inset(pts, 0.12), true);
    ctx.clip();
    let minY = Infinity;
    let maxY = -Infinity;
    let minX = Infinity;
    let maxX = -Infinity;
    for (const p of pts) {
      minY = Math.min(minY, p.y);
      maxY = Math.max(maxY, p.y);
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
    }
    // 3–4 parallel ripple lines across the lake.
    const rows = Math.max(3, Math.min(4, Math.floor((maxY - minY) / 0.35)));
    ctx.lineWidth = 1 * px;
    ctx.beginPath();
    for (let k = 1; k <= rows; k++) {
      const y = minY + ((maxY - minY) * k) / (rows + 1);
      ctx.moveTo(minX, y);
      ctx.lineTo(maxX, y);
    }
    ctx.stroke();
    ctx.restore();
  }

  // Streams: single wavy line
  ctx.globalAlpha = TERRAIN_ALPHA.stream;
  ctx.lineWidth = 1.4 * px;
  ctx.beginPath();
  for (const l of geo.streams) {
    const pts = resample(l.pts, l.closed, 0.05);
    pts.forEach((p, i) => {
      const t = tangentAt(pts, i);
      const w = Math.sin((i * 0.05 * Math.PI * 2) / 0.55) * 0.07;
      const x = p.x - t.y * w;
      const y = p.y + t.x * w;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
  }
  ctx.stroke();

  // Rivers: two parallel lines with a light ink wash between (no rungs: that reads as railroad)
  const riverW = Math.max(0.2, 5 * px);
  doubleLine(ctx, geo.rivers, riverW, s, TERRAIN_ALPHA.river);
  ctx.globalAlpha = TERRAIN_ALPHA.riverFill;
  ctx.strokeStyle = s.ink;
  ctx.lineWidth = Math.max(0, riverW - 1.2 * px);
  ctx.lineCap = 'butt';
  strokeLines(ctx, geo.rivers);
  ctx.lineCap = 'round';

  // Roads: two parallel solid lines (drawn over rivers, so a bridge's deck crosses cleanly)
  const roadW = Math.max(0.12, 4 * px);
  doubleLine(ctx, geo.roads, roadW, s, TERRAIN_ALPHA.road);

  // Bridges: brackets beside the road with outward ticks at both ends
  ctx.globalAlpha = TERRAIN_ALPHA.bridge;
  ctx.strokeStyle = s.ink;
  ctx.lineWidth = 1.5 * px;
  ctx.beginPath();
  const half = riverW / 2 + 0.08;
  const off = roadW / 2 + Math.max(0.035, 2.5 * px);
  const tick = Math.max(0.06, 4.5 * px);
  for (const b of geo.bridges) {
    const u = b.dir;
    const n = { x: -u.y, y: u.x };
    for (const sgn of [-1, 1]) {
      const ox = b.at.x + n.x * off * sgn;
      const oy = b.at.y + n.y * off * sgn;
      ctx.moveTo(
        ox - u.x * half + n.x * tick * sgn - u.x * tick,
        oy - u.y * half + n.y * tick * sgn - u.y * tick,
      );
      ctx.lineTo(ox - u.x * half, oy - u.y * half);
      ctx.lineTo(ox + u.x * half, oy + u.y * half);
      ctx.lineTo(
        ox + u.x * half + n.x * tick * sgn + u.x * tick,
        oy + u.y * half + n.y * tick * sgn + u.y * tick,
      );
    }
  }
  ctx.stroke();

  ctx.restore();

  // Map border
  ctx.save();
  ctx.globalAlpha = TERRAIN_ALPHA.border;
  ctx.strokeStyle = s.ink;
  ctx.lineWidth = 1.5 * px;
  ctx.lineJoin = 'round';
  outlinePath(ctx, geo);
  ctx.stroke();
  ctx.restore();
}

/** Ink stroke, then a narrower paper stroke: leaves two parallel lines. */
function doubleLine(
  ctx: CanvasRenderingContext2D,
  lines: Polyline[],
  width: number,
  s: TerrainStyle,
  alpha: number,
): void {
  if (lines.length === 0) return;
  const line = 1.2 * s.cssPx;
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = s.ink;
  ctx.lineWidth = width + line;
  ctx.lineCap = 'butt';
  strokeLines(ctx, lines);
  ctx.globalAlpha = 1;
  ctx.strokeStyle = s.paper;
  ctx.lineWidth = Math.max(0, width - line);
  strokeLines(ctx, lines);
  ctx.lineCap = 'round';
  ctx.strokeStyle = s.ink;
}

/** Index contours carry their height in meters (each level ≈ 10 m), on a paper halo. */
function drawContourLabels(
  ctx: CanvasRenderingContext2D,
  geo: TerrainGeometry,
  s: TerrainStyle,
): void {
  const px = s.cssPx;
  // Too small to read when zoomed far out: leave them off rather than draw specks.
  if (1 / px < 16) return;
  const size = 11 * px;
  ctx.save();
  ctx.font = `600 ${size}px ${s.font}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const c of geo.contours) {
    if (!c.index) continue;
    const pts = resample(c.pts, c.closed, 0.25);
    if (pts.length < 8) continue;
    const i = Math.floor(pts.length / 2);
    const p = pts[i] as Point;
    // Skip labels that would sit outside the map.
    if (!insidePolygon(p, geo.outline)) continue;
    const t = tangentAt(pts, i);
    // Map convention: the top of the numerals points uphill.
    const nx = -t.y;
    const ny = t.x;
    const leftHigher =
      geo.elevAt({ x: p.x + nx * 0.5, y: p.y + ny * 0.5 }) >=
      geo.elevAt({ x: p.x - nx * 0.5, y: p.y - ny * 0.5 });
    // Text "up" after rotating by ang is (sin ang, −cos ang); with ang = atan2(t) that is (t.y, −t.x) = −n.
    let ang = Math.atan2(t.y, t.x);
    if (leftHigher) ang += Math.PI;
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(ang);
    const text = String(c.level * 10);
    ctx.globalAlpha = 1;
    ctx.lineWidth = 4 * px;
    ctx.strokeStyle = s.paper;
    ctx.strokeText(text, 0, 0);
    ctx.globalAlpha = TERRAIN_ALPHA.contourLabel;
    ctx.fillStyle = s.ink;
    ctx.fillText(text, 0, 0);
    ctx.restore();
  }
  ctx.restore();
}

function insidePolygon(p: Point, poly: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i] as Point;
    const b = poly[j] as Point;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) {
      inside = !inside;
    }
  }
  return inside;
}
